/**
 * Agent UI service for client-side operations
 * Provides API calls for agent form interface CRUD and AI schema generation
 */

import { api } from "../utils/api-client";
import type {
  AgentUiRecord,
  FormSchema,
  DescriptionGenerationResponse,
  AgentGenerationResponse,
  RefinementResponse,
} from "../types/agent-ui-types";

/** Wrapped API response shape from server */
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Fetches all UI interfaces for an agent
 */
export async function getAgentUis(
  agentId: string
): Promise<AgentUiRecord[]> {
  const response: ApiResponse<AgentUiRecord[]> = await api.get(
    `/agents/${agentId}/uis`
  );
  return response.data;
}

/**
 * Fetches a single UI interface by ID
 */
export async function getAgentUiById(
  agentId: string,
  uiId: string
): Promise<AgentUiRecord> {
  const response: ApiResponse<AgentUiRecord> = await api.get(
    `/agents/${agentId}/uis/${uiId}`
  );
  return response.data;
}

/**
 * Creates a new UI interface for an agent
 */
export async function createAgentUi(
  agentId: string,
  data: {
    name: string;
    description?: string;
    formSchema: FormSchema;
    naturalLanguagePrompt?: string;
  }
): Promise<AgentUiRecord> {
  const response: ApiResponse<AgentUiRecord> = await api.post(
    `/agents/${agentId}/uis`,
    data
  );
  return response.data;
}

/**
 * Updates an existing UI interface
 */
export async function updateAgentUi(
  agentId: string,
  uiId: string,
  data: {
    name?: string;
    description?: string | null;
    formSchema?: FormSchema;
    naturalLanguagePrompt?: string | null;
  }
): Promise<AgentUiRecord> {
  const response: ApiResponse<AgentUiRecord> = await api.put(
    `/agents/${agentId}/uis/${uiId}`,
    data
  );
  return response.data;
}

/**
 * Deletes a UI interface
 */
export async function deleteAgentUi(
  agentId: string,
  uiId: string
): Promise<void> {
  await api.delete(`/agents/${agentId}/uis/${uiId}`);
}

/**
 * Toggles the published status of a UI interface
 */
export async function togglePublish(
  agentId: string,
  uiId: string
): Promise<AgentUiRecord> {
  const response: ApiResponse<AgentUiRecord> = await api.post(
    `/agents/${agentId}/uis/${uiId}/publish`
  );
  return response.data;
}

/**
 * Sets a UI as the default interface for the agent
 */
export async function setDefaultUi(
  agentId: string,
  uiId: string
): Promise<AgentUiRecord> {
  const response: ApiResponse<AgentUiRecord> = await api.post(
    `/agents/${agentId}/uis/${uiId}/default`
  );
  return response.data;
}

/**
 * Generates a form schema from a natural language description using AI
 */
export async function generateSchemaFromDescription(
  agentId: string,
  description: string,
  agentContext?: { name?: string; description?: string }
): Promise<DescriptionGenerationResponse> {
  const response: ApiResponse<DescriptionGenerationResponse> = await api.post(
    `/agents/${agentId}/uis/generate-schema/description`,
    { description, agentContext }
  );
  return response.data;
}

/**
 * Auto-generates a form schema by analyzing the agent's configuration
 */
export async function generateSchemaFromAgent(
  agentId: string
): Promise<AgentGenerationResponse> {
  const response: ApiResponse<AgentGenerationResponse> = await api.post(
    `/agents/${agentId}/uis/generate-schema/agent`
  );
  return response.data;
}

/**
 * Refines an existing form schema with a natural language instruction
 */
export async function refineSchema(
  agentId: string,
  currentSchema: FormSchema,
  instruction: string
): Promise<RefinementResponse> {
  const response: ApiResponse<RefinementResponse> = await api.post(
    `/agents/${agentId}/uis/generate-schema/refine`,
    { currentSchema, instruction }
  );
  return response.data;
}
